import { EntityType, GetOrganizationResponse, GetPublicOrganizationResponse, ObjectId, OrganizationPermissionLevel, OrganizationTeamMember, SubscriptionFeature, UserInfo } from '../../types';
import { getOrgTeamMembers, getOrganization, getPublicOrganizations } from '../api/organization-api';
import { isDefined, setLocalStorage } from '../utils';
import { logWithAmplitude, setGoogleOrganizationId } from '../api/api';
import { clearOrgCookie, getOrgCookie, setOrgCookie } from '../auth-cookie';

import { State } from './state-management/state';
import accountInfo from './account-info';
import {
getOrganizationRoles,
} from '../api/account-api';
import { getRolesForEntity } from '../api/roles-api';
import { property } from './state-management/decorators/property';
import { state } from 'lit/decorators';

class OrganizationInfo extends State {
    @property({ type: Boolean }) isReady: boolean = false;

    @property({ type: Object }) organization?: GetOrganizationResponse;

    @property({ type: Array }) allOrganizations: GetPublicOrganizationResponse[] = [];

    @property({ type: Map }) enabledFeatures: SubscriptionFeature[] = [];

    @property({ type: Boolean }) isGuest: boolean = false;

    @state() chargeableMembers: OrganizationTeamMember[] = [];

    @state() allMembers: OrganizationTeamMember[] = [];

    id: ObjectId | undefined = { $oid: '' };

    static amplitude_callsite: string = 'Organization Info Cache';

    // call this in sign in page, and then in main-page. in main-page, check if data isReady, if not, instantiate
    async instantiateOrganizationDataAsAuthenticatedUser(organization_id?: string) {
        this.isReady = false;

        const org_id = organization_id ?? this.id?.$oid ?? localStorage.getItem('organization_id') ?? getOrgCookie();

        if (!accountInfo.isAuthenticated || !isDefined(org_id)) {
            this.clearOrganizationData();
            console.error('Cannot instantiate organization data as user without being logged in');
            return;
        }

        const organization = await this.fetchOrganizationInfo(org_id);
        if (!isDefined(organization)) {
            this.clearOrganizationData();
            return;
        }

        this.organization = organization;
        this.id = { $oid: org_id };
        setLocalStorage('organization_id', this.id.$oid);
        setOrgCookie(this.id.$oid);

        // if the user is not a full teammate, dont continue setting up the organization and subscription because they shouldnt have access to advanced features
        if (!this.organization.is_collaborator) {
            this.isGuest = true;
            this.organization.chargebee_subscription = undefined;
            this.organization.enabled_features = [];
            return;
        } else {
            this.isGuest = false;
        }

        this.enabledFeatures = this.organization?.enabled_features ?? [];

        logWithAmplitude(OrganizationInfo.amplitude_callsite, 'Organization Subscription Status', {
            subscription_id: this.organization?.chargebee_subscription?.subscription_id,
            chargebee_customer_id: this.organization?.chargebee_subscription?.customer_id,
            chargebee_subscription_plan: this.organization?.chargebee_subscription?.plan_id,
            login_type: 'organization'
        });

        try {
            await this.getChargeableTeamMembers();
        } catch (err) {
            console.error('Error fetching chargeable team members', err);
        }
        await this.getAllOrganizations();

        this.isReady = true;

        setGoogleOrganizationId(this.organization?._id.$oid, this.organization?.name ?? 'Unknown');
    }

    async getChargeableTeamMembers() {
        await this.getOrganizationMembers();
        const members = this.allMembers.filter((member) => !member.free);
        this.chargeableMembers = members.filter((role) => role?.role.OrganizationRoleType?.level !== 'LIMITED');
    }

    async getAllOrganizations() {
        try {
            const rolesData = await getOrganizationRoles(
                OrganizationInfo.amplitude_callsite,
                accountInfo.email,
            );
            if (rolesData?.length > 0) {
                const organizationIds = rolesData.map(role => role.role?.OrganizationRoleType?.organization_id?.$oid);
                getPublicOrganizations(OrganizationInfo.amplitude_callsite, organizationIds)
                    .then(organizations => {
                        this.allOrganizations = organizations.filter((org): org is GetPublicOrganizationResponse => org !== undefined);
                    })
                    .catch(err => {
                        console.error('Error fetching organizations:', err);
                        // Handle the error appropriately
                    });
            }
            // if an organization is not set, set the first one
            if (!this.id && this.allOrganizations.length > 0 && accountInfo.isAuthenticated) {
                const firstOrganization = this.allOrganizations[0];
                this.id = firstOrganization._id;
                setLocalStorage('organization_id', this.id.$oid);
                await this.instantiateOrganizationDataAsAuthenticatedUser(this.id.$oid);
            } else if (this.allOrganizations.length === 0) {
                this.allOrganizations = [];
            }
        } catch(err) {
            this.allOrganizations = [];
        }
    }

    async fetchOrganizationInfo(organizationId?: string) {
        if (!isDefined(organizationId)) {
            this.clearOrganizationData();
            return undefined;
        }
        try {
            const organizationInfo = await getOrganization(OrganizationInfo.amplitude_callsite, organizationId);
            if (isDefined(organizationInfo)) {
                return organizationInfo; // Return organizationInfo if successful
            } else {
                this.clearOrganizationData();
                return undefined;
            }
        } catch (err) {
            this.clearOrganizationData();
            return undefined;
        }
    }

    clearOrganizationData() {
        this.organization = undefined;
        this.id = undefined;
        localStorage.removeItem('organization_id');
        clearOrgCookie();
        this.isReady = true;
    }

    async getOrganizationMembers() {
        this.allMembers = await getOrgTeamMembers(
                OrganizationInfo.amplitude_callsite,
               this.id?.$oid ?? ''
            );
        }

    getUserOrganizationRoleLevel(email: string) {
        if (!this.isReady) {
            return undefined;
        }
        return this.allMembers.find((member) => member.email === email);
    }

    get allUsersAsUserInfos() {
        return this.allMembers.map((member) => ({
            email: member.email,
            name: member.name,
            avatar: member.avatar,
        })) as UserInfo[];
    }

    get numberOfAdmins() {
        return this.allMembers.filter((member) => member.role.OrganizationRoleType?.level === OrganizationPermissionLevel.ADMINISTRATOR).length;
    }

    async getOrganizationRoles() {
        if (!this.isReady || !this.id || !accountInfo.isAuthenticated) {
            return [];
        }
        await this.getOrganizationMembers();
        return (await getRolesForEntity(
            OrganizationInfo.amplitude_callsite,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.id?.$oid,
            EntityType.Organization
        )).filter((role) => !role.email.includes('dummy.com'));
    }
}

const organizationInfo = new OrganizationInfo();

export default organizationInfo;
